import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Gift, Trophy, Crown, Star, Loader2, CheckCircle, Pencil, Medal } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecompensasPage = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBonus, setEditingBonus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    quarter: '',
    bonus_amount: '',
    bonus_second: '',
    bonus_third: '',
    description: ''
  });

  useEffect(() => {
    fetchBonuses();
  }, []);

  const fetchBonuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/bonuses`, {
        headers: getAuthHeaders()
      });
      setBonuses(response.data);
    } catch (error) {
      toast.error('Error al cargar bonos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        bonus_amount: parseFloat(formData.bonus_amount),
        bonus_second: formData.bonus_second ? parseFloat(formData.bonus_second) : null,
        bonus_third: formData.bonus_third ? parseFloat(formData.bonus_third) : null,
        description: formData.description || null
      };

      if (editingBonus) {
        await axios.put(`${API_URL}/bonuses/${editingBonus.id}`, payload, { 
          headers: getAuthHeaders() 
        });
        toast.success('Bono actualizado exitosamente');
      } else {
        payload.quarter = formData.quarter;
        await axios.post(`${API_URL}/bonuses`, payload, { 
          headers: getAuthHeaders() 
        });
        toast.success('Bono creado exitosamente');
      }
      
      fetchBonuses();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar bono');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (bonus) => {
    setEditingBonus(bonus);
    setFormData({
      quarter: bonus.quarter,
      bonus_amount: bonus.bonus_amount.toString(),
      bonus_second: bonus.bonus_second?.toString() || '',
      bonus_third: bonus.bonus_third?.toString() || '',
      description: bonus.description || ''
    });
    setIsDialogOpen(true);
  };

  const handleClaim = async (bonusId) => {
    try {
      await axios.put(`${API_URL}/bonuses/${bonusId}/claim`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Bono marcado como reclamado');
      fetchBonuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reclamar');
    }
  };

  const handleDelete = async (bonusId) => {
    try {
      await axios.delete(`${API_URL}/bonuses/${bonusId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Bono eliminado');
      fetchBonuses();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingBonus(null);
    setFormData({
      quarter: '',
      bonus_amount: '',
      bonus_second: '',
      bonus_third: '',
      description: ''
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getCurrentQuarter = () => {
    const now = new Date();
    const quarter = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${quarter}-${now.getFullYear()}`;
  };

  const getQuarterOptions = () => {
    const options = [];
    const now = new Date();
    const year = now.getFullYear();
    
    for (let q = 1; q <= 4; q++) {
      options.push(`Q${q}-${year}`);
    }
    for (let q = 1; q <= 4; q++) {
      options.push(`Q${q}-${year + 1}`);
    }
    return options;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recompensas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recompensas Trimestrales
          </h1>
          <p className="text-slate-500 mt-1">Bonos para los 3 mejores gestores de cada trimestre</p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={() => {
              setFormData({ ...formData, quarter: getCurrentQuarter() });
              setIsDialogOpen(true);
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
            data-testid="add-bonus-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Bono
          </Button>
        )}
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-yellow-400 rounded-xl">
              <Trophy className="w-8 h-8 text-yellow-900" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">¿Cómo funcionan las recompensas?</h3>
              <p className="text-slate-600 mt-1">
                Cada trimestre, los <strong>3 gestores con mayores ventas</strong> ganan bonos. 
                El 1er lugar obtiene el premio principal, el 2do y 3er lugar obtienen premios adicionales si están configurados.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bonuses Grid */}
      {bonuses.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-8 text-center text-slate-500">
            <Gift className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No hay bonos configurados aún</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {bonuses.map((bonus) => (
            <Card 
              key={bonus.id} 
              className={`shadow-sm overflow-hidden ${bonus.is_claimed ? 'opacity-75' : ''}`}
              data-testid={`bonus-card-${bonus.id}`}
            >
              {/* Header with quarter */}
              <div className={`p-4 ${bonus.is_claimed ? 'bg-slate-100' : 'bg-gradient-to-r from-yellow-400 to-amber-400'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className={`w-6 h-6 ${bonus.is_claimed ? 'text-slate-500' : 'text-yellow-900'}`} />
                    <span className={`text-xl font-bold ${bonus.is_claimed ? 'text-slate-600' : 'text-yellow-900'}`}>
                      {bonus.quarter}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {bonus.is_claimed ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Reclamado
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-900 text-yellow-100">Activo</Badge>
                    )}
                    {isAdmin() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(bonus)}
                        className="h-8 w-8"
                        title="Editar bono"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <CardContent className="p-5 space-y-4">
                {/* Top 3 Winners */}
                <div className="space-y-3">
                  {/* 1st Place */}
                  <div className={`p-4 rounded-lg ${bonus.winner_gestor_name ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center">
                          <Trophy className="w-5 h-5 text-yellow-900" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">1ER LUGAR</p>
                          {bonus.winner_gestor_name ? (
                            <>
                              <p className="font-semibold text-slate-900">{bonus.winner_gestor_name}</p>
                              <p className="text-xs text-slate-500">Ventas: {formatCurrency(bonus.winner_sales)}</p>
                            </>
                          ) : (
                            <p className="text-slate-500 text-sm">Sin datos</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">{formatCurrency(bonus.bonus_amount)}</p>
                      </div>
                    </div>
                  </div>

                  {/* 2nd Place */}
                  <div className={`p-3 rounded-lg ${bonus.second_gestor_name ? 'bg-slate-100' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center">
                          <Medal className="w-4 h-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">2DO LUGAR</p>
                          {bonus.second_gestor_name ? (
                            <>
                              <p className="font-medium text-slate-800 text-sm">{bonus.second_gestor_name}</p>
                              <p className="text-xs text-slate-500">Ventas: {formatCurrency(bonus.second_sales)}</p>
                            </>
                          ) : (
                            <p className="text-slate-400 text-sm">-</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{bonus.bonus_second ? formatCurrency(bonus.bonus_second) : '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* 3rd Place */}
                  <div className={`p-3 rounded-lg ${bonus.third_gestor_name ? 'bg-amber-50' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                          <Star className="w-4 h-4 text-amber-700" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">3ER LUGAR</p>
                          {bonus.third_gestor_name ? (
                            <>
                              <p className="font-medium text-slate-800 text-sm">{bonus.third_gestor_name}</p>
                              <p className="text-xs text-slate-500">Ventas: {formatCurrency(bonus.third_sales)}</p>
                            </>
                          ) : (
                            <p className="text-slate-400 text-sm">-</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{bonus.bonus_third ? formatCurrency(bonus.bonus_third) : '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {bonus.description && (
                  <p className="text-sm text-slate-600 italic border-t pt-3">"{bonus.description}"</p>
                )}

                {/* Actions - Admin only */}
                {isAdmin() && (
                  <div className="flex gap-2 pt-2 border-t">
                    {!bonus.is_claimed && bonus.winner_gestor_name && (
                      <Button
                        size="sm"
                        onClick={() => handleClaim(bonus.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Marcar Reclamado
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(bonus.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Bonus Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-yellow-500" />
              {editingBonus ? 'Editar Bono Trimestral' : 'Crear Nuevo Bono Trimestral'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Quarter - solo para crear */}
            {!editingBonus ? (
              <div className="space-y-2">
                <Label>Trimestre *</Label>
                <select
                  value={formData.quarter}
                  onChange={(e) => setFormData({ ...formData, quarter: e.target.value })}
                  className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50"
                  required
                >
                  <option value="">Selecciona trimestre</option>
                  {getQuarterOptions().map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">Trimestre: <strong>{editingBonus.quarter}</strong></p>
              </div>
            )}

            {/* Prizes for top 3 */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  Premio 1er Lugar (USD) *
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.bonus_amount}
                  onChange={(e) => setFormData({ ...formData, bonus_amount: e.target.value })}
                  placeholder="1000"
                  required
                  className="bg-slate-50"
                  data-testid="bonus-first-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Medal className="w-4 h-4 text-slate-400" />
                  Premio 2do Lugar (USD)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.bonus_second}
                  onChange={(e) => setFormData({ ...formData, bonus_second: e.target.value })}
                  placeholder="500 (opcional)"
                  className="bg-slate-50"
                  data-testid="bonus-second-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  Premio 3er Lugar (USD)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.bonus_third}
                  onChange={(e) => setFormData({ ...formData, bonus_third: e.target.value })}
                  placeholder="250 (opcional)"
                  className="bg-slate-50"
                  data-testid="bonus-third-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Bono por mejor desempeño..."
                className="bg-slate-50"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || (!editingBonus && !formData.quarter)}
                className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                data-testid="save-bonus-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingBonus ? 'Guardar Cambios' : 'Crear Bono')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecompensasPage;
